/**
 * Recognised option-names strip (Phase 65 / BLU-594 PR2). The fixed aliases the
 * derivation treats as the colour / size dimension. Any alias SEEN in the
 * store's preview is highlighted emerald (a done/recognised state); the rest
 * render neutral. If nothing is derivable yet, all render without highlights.
 */
import { useTranslation } from 'react-i18next';
import { COLOUR_OPTION_NAMES, SIZE_OPTION_NAMES } from '@threadheads/shared';
import { cn } from '@/lib/utils';

interface OptionNamesStripProps {
  /** Lowercased option names observed across the current preview page. */
  seenOptionNames: Set<string>;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function OptionNamesStrip({ seenOptionNames }: OptionNamesStripProps) {
  const { t } = useTranslation('productSync');

  const renderAliases = (names: readonly string[]) =>
    names.map((name) => {
      const seen = seenOptionNames.has(name.toLowerCase());
      return (
        <span
          key={name}
          className={cn(
            'rounded border px-1.5 py-0.5 font-mono text-[11px]',
            seen ? 'border-emerald-300 bg-emerald-100 font-semibold text-emerald-800' : 'border-border bg-muted/60 text-foreground',
          )}
        >
          {cap(name)}
        </span>
      );
    });

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border bg-background px-4 py-3">
      <span className="text-xs font-bold">{t('options.title')}</span>
      <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {t('options.colourFrom')} <span aria-hidden>←</span> {renderAliases(COLOUR_OPTION_NAMES)}
      </span>
      <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {t('options.sizeFrom')} <span aria-hidden>←</span> {renderAliases(SIZE_OPTION_NAMES)}
      </span>
      <span className="max-w-md text-[11px] text-muted-foreground/80">{t('options.seenHint')}</span>
    </div>
  );
}
